import React, { useEffect, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Document } from '../../types/Document';
import { dataService } from '../../services/DataService';
import DocumentForm from './DocumentForm';

interface ModuleDocumentsProps {
  moduleId: string;
}

export default function ModuleDocuments({ moduleId }: ModuleDocumentsProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadDocuments = async () => {
    setIsLoading(true);
    const res = await dataService.getItems(`modules/${moduleId}/documents`);
    if (res.statusCode === 200) {
      const docsData = res.body.map((doc: any) => ({
        ...doc,
        id: doc.id || doc.docId || doc.documentId,
      }));
      setDocuments(docsData);
    }
    setIsLoading(false);
  };

  const handleSave = async (document: Document, files?: { de?: File; fr?: File; it?: File }) => {
    try {
      let storageUrl = document.storageUrl;
      let storageUrl_fr = document.storageUrl_fr;
      let storageUrl_it = document.storageUrl_it;

      try {
        // Sequential single-file uploads to avoid large multipart body limits in Netlify CLI
        if (files?.de) {
          const uploadPathDe = `modules/${moduleId}/documents/de/${files.de.name}`;
          const resDe = await dataService.uploadFile(files.de, uploadPathDe);
          storageUrl = resDe.url;
        }
        if (files?.fr) {
          const uploadPathFr = `modules/${moduleId}/documents/fr/${files.fr.name}`;
          const resFr = await dataService.uploadFile(files.fr, uploadPathFr);
          storageUrl_fr = resFr.url;
        }
        if (files?.it) {
          const uploadPathIt = `modules/${moduleId}/documents/it/${files.it.name}`;
          const resIt = await dataService.uploadFile(files.it, uploadPathIt);
          storageUrl_it = resIt.url;
        }
      } catch (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error('Failed to upload file(s)');
      }

      const documentData = {
        ...document,
        storageUrl,
        storageUrl_fr,
        storageUrl_it,
      };

      if (isEditing && isEditing !== 'new') {
        await dataService.updateItem(`modules/${moduleId}/documents`, document.id, documentData);
      } else {
        await dataService.setItem(`modules/${moduleId}/documents`, undefined, {
          ...documentData,
          moduleId,
          order: documents.length,
        });
      }
      setIsEditing(null);
      loadDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
    }
  };

  const handleDelete = async (doc: Document) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        if (doc.storageUrl) await dataService.deleteFile(doc.storageUrl);
        if (doc.storageUrl_fr) await dataService.deleteFile(doc.storageUrl_fr);
        if (doc.storageUrl_it) await dataService.deleteFile(doc.storageUrl_it);
        await dataService.deleteItem(`modules/${moduleId}/documents`, doc.id);
        loadDocuments();
      } catch (error) {
        console.error('Error deleting document:', error);
      }
    }
  };

  const getEmptyDocument = (): Document => ({
    id: '',
    moduleId,
    order: documents.length,
    filename: '',
    filename_fr: '',
    filename_it: '',
    storageUrl: '',
    storageUrl_fr: '',
    storageUrl_it: '',
    title: '',
    title_fr: '',
    title_it: '',
  });

  const reorderDocuments = async (items: Document[]) => {
    try {
      const reorderData = {
        collection: `modules/${moduleId}/documents`,
        items: items.map((doc, index) => ({
          id: doc.id,
          order: index,
        })),
      };

      await dataService.reorderItems(reorderData);
      loadDocuments();
    } catch (error) {
      console.error('Error reordering documents:', error);
    }
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(documents);
    const [reorderedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, reorderedItem);

    setDocuments(reorderedItems);
    reorderDocuments(reorderedItems);
  };

  useEffect(() => {
    loadDocuments();
  }, [moduleId]);

  if (isLoading) {
    return <div>Loading documents...</div>;
  }

  return (
    <Row>
      <Col>
        {isEditing ? (
          <DocumentForm
            initialValues={
              isEditing === 'new'
                ? getEmptyDocument()
                : documents.find((d) => d.id === isEditing) || getEmptyDocument()
            }
            onSave={handleSave}
            onCancel={() => setIsEditing(null)}
          />
        ) : (
          <>
            <div className="mb-3">
              <Button onClick={() => setIsEditing('new')}>Add New Document</Button>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="documents">
                {(provided) => (
                  <ul
                    className="item-list no-list-style"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {documents
                      .sort((a, b) => a.order - b.order)
                      .map((doc, index) => (
                        <Draggable
                          key={doc.id}
                          draggableId={doc.id}
                          index={index}
                        >
                          {(dragProvided) => (
                            <li
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className="list-item p-3 mb-2 border rounded"
                            >
                              <div className="d-flex align-items-center">
                                <span className="me-3">⋮⋮</span>
                                <div className="flex-grow-1">
                                  <div>
                                    <div className="mb-2">
                                      DE:
                                      {' '}
                                      <a
                                        href={doc.storageUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {doc.title}
                                      </a>
                                    </div>
                                    <div className="mb-2">
                                      FR:
                                      <br />
                                      {doc.storageUrl_fr ? (
                                        <a
                                          href={doc.storageUrl_fr}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {doc.title_fr || doc.filename_fr || (doc.storageUrl_fr.split('/').pop() || 'Dokument (FR)')}
                                        </a>
                                      ) : (
                                        <span className="text-muted">Kein Dokument (FR)</span>
                                      )}
                                    </div>
                                    <div className="mb-2">
                                      IT:
                                      <br />
                                      {doc.storageUrl_it ? (
                                        <a
                                          href={doc.storageUrl_it}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {doc.title_it || doc.filename_it || (doc.storageUrl_it.split('/').pop() || 'Documento (IT)')}
                                        </a>
                                      ) : (
                                        <span className="text-muted">Kein Dokument (IT)</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="mt-3">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => doc.id && setIsEditing(doc.id)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDelete(doc)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </li>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </Col>
    </Row>
  );
}
